// PDP product rows: "Recommended for you", "Best Sellers" and "Recently viewed".
// A product shows once across the page: the row higher up keeps it, so each row waits for the
// one above to settle before it excludes what that row already shows.
(() => {
    // Every row section renders the script tag: only the first run defines anything.
    if (customElements.get('product-row')) return;

    const RESULTS_SECTION = 'pdp-recommendation-results';
    const CARD_SECTION = 'pdp-product-card';
    const HISTORY_KEY = 'ogk:recently-viewed';
    const HISTORY_LIMIT = 15;
    // Recommended can chain up to four requests: starting well before the row is reached
    // keeps the loading placeholders out of sight on a normal scroll.
    const NEAR_VIEWPORT = '0px 0px 1200px 0px';
    const IDLE_FALLBACK_MS = 3000;

    const shownIds = new Set();

    const createSettled = () => {
        let resolve;
        const promise = new Promise((done) => {
            resolve = done;
        });
        return { promise, resolve };
    };
    const recommendationsSettled = createSettled();
    const bestSellersSettled = createSettled();

    const normalise = (value) => (value || '').trim().toLowerCase();

    const sectionOf = (element) => element.closest('.shopify-section') || element;

    // Same reasons as ProductRecommendations in global.js: a jump in the scroll position can move
    // the row past the viewport without the observer ever reporting it, so idle time loads it too.
    const whenNearViewport = (element) =>
        new Promise((resolve) => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;
                    observer.disconnect();
                    resolve();
                },
                { rootMargin: NEAR_VIEWPORT },
            );
            observer.observe(element);

            const loadWhenIdle = () => {
                const done = () => {
                    observer.disconnect();
                    resolve();
                };
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(done, { timeout: IDLE_FALLBACK_MS });
                } else {
                    setTimeout(done, IDLE_FALLBACK_MS);
                }
            };
            if (document.readyState === 'complete') {
                loadWhenIdle();
            } else {
                window.addEventListener('load', loadWhenIdle, { once: true });
            }
        });

    const fetchRowItems = async (url) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status} ${url}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...doc.querySelectorAll('[data-row-item]')];
    };

    class ProductRow extends HTMLElement {
        connectedCallback() {
            this.track = this.querySelector('[data-row-track]');
            this.prevButton = this.querySelector('[data-row-prev]');
            this.nextButton = this.querySelector('[data-row-next]');
            if (!this.track || !this.prevButton || !this.nextButton) return;

            this.prevButton.addEventListener('click', () => this.page(-1));
            this.nextButton.addEventListener('click', () => this.page(1));
            this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
            new ResizeObserver(() => this.updateArrows()).observe(this.track);
        }

        page(direction) {
            this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
        }

        updateArrows() {
            const { scrollLeft, scrollWidth, clientWidth } = this.track;
            const maxScroll = scrollWidth - clientWidth;
            const scrollable = maxScroll > 1;
            this.prevButton.hidden = !scrollable;
            this.nextButton.hidden = !scrollable;
            this.prevButton.disabled = scrollLeft <= 1;
            this.nextButton.disabled = scrollLeft >= maxScroll - 1;
        }

        get items() {
            return [...this.track.querySelectorAll('[data-row-item]')];
        }

        setItems(items) {
            this.track.replaceChildren(...items);
            this.track.scrollLeft = 0;
            this.updateArrows();
        }
    }

    class PdpRecommendations extends HTMLElement {
        connectedCallback() {
            this.row = this.querySelector('product-row');
            shownIds.add(this.dataset.productId);

            whenNearViewport(this)
                .then(() => this.load())
                .catch((error) => {
                    console.error('[pdp-recommendations]', error);
                    sectionOf(this).hidden = true;
                })
                .finally(() => recommendationsSettled.resolve());
        }

        priceBand(tolerance) {
            const price = Number(this.dataset.price);
            return {
                min: Math.floor((price * (100 - tolerance)) / 100),
                max: Math.ceil((price * (100 + tolerance)) / 100),
            };
        }

        collectionUrl(vendor, { min, max }) {
            const params = new URLSearchParams({
                section_id: RESULTS_SECTION,
                sort_by: 'best-selling',
                'filter.v.availability': '1',
                'filter.v.price.gte': (min / 100).toFixed(2),
                'filter.v.price.lte': (max / 100).toFixed(2),
            });
            if (vendor) params.set('filter.p.vendor', vendor);
            return `${this.dataset.collectionUrl}?${params}`;
        }

        nativeUrl() {
            const params = new URLSearchParams({
                product_id: this.dataset.productId,
                limit: this.dataset.limit,
                intent: 'related',
                section_id: RESULTS_SECTION,
            });
            return `${this.dataset.recommendationsUrl}?${params}`;
        }

        async fetchOrEmpty(url) {
            try {
                return await fetchRowItems(url);
            } catch (error) {
                console.error('[pdp-recommendations] request failed, continuing with the rest', error);
                return [];
            }
        }

        // Tiers 1 and 2 come from one request (same vendor, wide price range: the narrow range is a
        // subset of it) and tier 3 from the same range without the vendor. Both run in parallel;
        // Shopify's related recommendations are requested only when the row is still short.
        async load() {
            const limit = Number(this.dataset.limit);
            const { vendor, productType } = this.dataset;
            const picked = [];
            const pickedIds = new Set();

            const take = (items, matches) => {
                for (const item of items) {
                    if (picked.length >= limit) return;
                    const id = item.dataset.productId;
                    if (shownIds.has(id) || pickedIds.has(id) || !matches(item)) continue;
                    picked.push(item);
                    pickedIds.add(id);
                }
            };

            if (productType && Number(this.dataset.price) > 0) {
                const narrow = this.priceBand(Number(this.dataset.tolerance));
                const wide = this.priceBand(Number(this.dataset.toleranceWide));
                const anyVendorRequest = this.fetchOrEmpty(this.collectionUrl(null, wide));
                const sameVendorRequest = vendor ? this.fetchOrEmpty(this.collectionUrl(vendor, wide)) : anyVendorRequest;
                const [sameVendor, anyVendor] = await Promise.all([sameVendorRequest, anyVendorRequest]);

                const within = ({ min, max }) => (item) => {
                    const price = Number(item.dataset.productPrice);
                    return normalise(item.dataset.productType) === normalise(productType) && price >= min && price <= max;
                };
                take(sameVendor, within(narrow));
                take(sameVendor, within(wide));
                take(anyVendor, within(wide));
            }

            if (picked.length < limit) {
                take(await this.fetchOrEmpty(this.nativeUrl()), () => true);
            }

            if (!picked.length) {
                sectionOf(this).hidden = true;
                return;
            }
            pickedIds.forEach((id) => shownIds.add(id));
            this.row.setItems(picked);
        }
    }

    class PdpBestSellers extends HTMLElement {
        connectedCallback() {
            this.row = this.querySelector('product-row');
            shownIds.add(this.dataset.productId);

            recommendationsSettled.promise
                .then(() => this.excludeShown())
                .finally(() => bestSellersSettled.resolve());
        }

        excludeShown() {
            const limit = Number(this.dataset.limit);
            const visible = [];
            for (const item of this.row.items) {
                const show = visible.length < limit && !shownIds.has(item.dataset.productId);
                item.hidden = !show;
                if (show) visible.push(item);
            }

            if (!visible.length) {
                sectionOf(this).hidden = true;
                return;
            }
            visible.forEach((item) => shownIds.add(item.dataset.productId));
            this.row.updateArrows();
        }
    }

    const readHistory = () => {
        try {
            const entries = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
            return Array.isArray(entries) ? entries.filter((entry) => entry?.id && entry?.handle) : [];
        } catch (error) {
            console.warn('[pdp-recently-viewed] history unreadable, starting a new one', error);
            return [];
        }
    };

    const writeHistory = (entries) => {
        try {
            localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
        } catch (error) {
            console.warn('[pdp-recently-viewed] history not saved', error);
        }
    };

    class PdpRecentlyViewed extends HTMLElement {
        connectedCallback() {
            this.row = this.querySelector('product-row');
            const current = { id: this.dataset.productId, handle: this.dataset.productHandle };
            const history = readHistory().filter((entry) => String(entry.id) !== current.id);
            writeHistory([current, ...history].slice(0, HISTORY_LIMIT));

            if (!history.length) return;
            this.hidden = false;

            Promise.all([bestSellersSettled.promise, whenNearViewport(this)])
                .then(() => this.load(history))
                .catch((error) => {
                    console.error('[pdp-recently-viewed]', error);
                    this.hidden = true;
                });
        }

        async load(history) {
            const limit = Number(this.dataset.limit);
            const root = window.Shopify?.routes?.root || '/';
            const candidates = history.filter((entry) => !shownIds.has(String(entry.id)));

            const results = await Promise.allSettled(
                candidates.map((entry) =>
                    fetchRowItems(`${root}products/${encodeURIComponent(entry.handle)}?section_id=${CARD_SECTION}`),
                ),
            );
            const items = results
                .flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
                .filter((item) => !shownIds.has(item.dataset.productId))
                .slice(0, limit);

            if (!items.length) {
                this.hidden = true;
                return;
            }
            items.forEach((item) => shownIds.add(item.dataset.productId));
            this.row.setItems(items);
        }
    }

    customElements.define('product-row', ProductRow);
    customElements.define('pdp-recommendations', PdpRecommendations);
    customElements.define('pdp-best-sellers', PdpBestSellers);
    customElements.define('pdp-recently-viewed', PdpRecentlyViewed);

    // A row that is not on the page (section removed or disabled) must not hold up the ones below.
    if (!document.querySelector('pdp-recommendations')) recommendationsSettled.resolve();
    if (!document.querySelector('pdp-best-sellers')) bestSellersSettled.resolve();
})();
